import type { EventNode, TypedEvent } from "./component-hierarchy"

interface ComponentNodeBinding {
  readonly node: EventNode<TypedEvent>
  readonly previous: ComponentNodeBinding | undefined
  active: boolean
}

const componentBindings: WeakMap<Node, ComponentNodeBinding> = new WeakMap()

/** Attach before insertion so connectedCallback can find its logical parent. */
export function attachComponentNode(
  host: Node,
  node: EventNode<TypedEvent>,
): () => void {
  const binding: ComponentNodeBinding = {
    node,
    previous: componentBindings.get(host),
    active: true,
  }
  componentBindings.set(host, binding)
  return (): void => {
    if (!binding.active) return
    binding.active = false
    if (componentBindings.get(host) !== binding) return
    let previous = binding.previous
    while (previous && !previous.active) previous = previous.previous
    if (previous) {
      componentBindings.set(host, previous)
    } else {
      componentBindings.delete(host)
    }
  }
}

/** Resolve across DOM and shadow boundaries without broadcasting application events. */
export function findComponentNode(host: Node): EventNode<TypedEvent> | null {
  let current: Node | null = host
  while (current) {
    let binding = componentBindings.get(current)
    while (binding) {
      if (binding.active && !binding.node.isDisposed) return binding.node
      binding = binding.previous
    }
    current =
      current.parentNode ??
      (current.nodeType === 11 && "host" in current
        ? (current as ShadowRoot).host
        : null)
  }
  return null
}
